import Table from '@/components/Table/Table';
import { ColumnDef } from '@/components/Table/TableHelpers';
import { styled } from '@mui/material/styles';
import TableActions from './TableActions';
import { PartnerSchema, PartnerType } from '@/lib/types';
import PartnerBadge from '@/components/PartnerBadge/PartnerBadge';
import { FilterSchema } from '@/components/ResourcesSidebar/ResourcesSidebarHelpers';
import PartnersFilters from './PartnersFilters';
import useHasUserRole from '@/hooks/useHasUserRole';
import { useState } from 'react';
import Button from '@/components/Button/Button';
import { FormattedMessage } from 'react-intl';
import TrashIcon from '@/assets/trash.svg?react';
import PlusIcon from '@/assets/plus.svg?react';
import AddPartnerModal from './AddPartnerModal';
import { AxiosResponse } from 'axios';
import { postGamesDetachPartner } from '@/lib/api';

type Data = {
  id: number;
  name: string;
  type: PartnerType;
};

const PartnersTable = ({
  gameId,
  isBespokeBranded,
  partners,
  filters,
  onFiltersChange,
  onChangePartners,
}: {
  gameId: number;
  isBespokeBranded: boolean;
  partners: PartnerSchema[];
  filters: Partial<FilterSchema>;
  onFiltersChange: (filters: Partial<FilterSchema>) => void;
  onChangePartners: () => void;
}) => {
  const [addPartnerModalOpen, setAddPartnerModalOpen] =
    useState(false);
  const [selectedPartners, setSelectedPartners] = useState<number[]>(
    [],
  );
  const columns: ColumnDef<Data>[] = [
    {
      field: 'name',
      label: 'Partner Name',
      width: '80%',
    },
    {
      field: 'type',
      label: 'Partner Type',
      render: (_row, value) => (
        <PartnerBadge type={value as PartnerType} />
      ),
      width: '20%',
    },
  ];
  const hasSuperAdminRole = useHasUserRole('Super Admin');
  const canManagePartners = hasSuperAdminRole && isBespokeBranded;

  const handleDelete = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const promises: Promise<AxiosResponse<PartnerSchema, any>>[] = [];
    selectedPartners.forEach((partnerId) => {
      promises.push(postGamesDetachPartner(gameId, partnerId));
    });
    Promise.all(promises).then(() => {
      onChangePartners();
    });
  };

  const handleAdd = () => {
    setAddPartnerModalOpen(true);
  };

  const handleAddPartnerSubmit = () => {
    setAddPartnerModalOpen(false);
    onChangePartners();
  };

  return (
    <>
      <TableActions
        filters={
          <PartnersFilters
            filters={filters}
            onFiltersChange={onFiltersChange}
          />
        }
        filtersValues={filters}
        actions={
          canManagePartners
            ? [
                <Button
                  disabled={selectedPartners.length === 0}
                  key="partner-delete"
                  variant="contained"
                  onClick={handleDelete}
                  aria-label="Delete Partner Button"
                >
                  <TrashIcon />
                  <FormattedMessage
                    id="delete"
                    defaultMessage="Delete"
                  />
                </Button>,
                <Button
                  key="partner-add"
                  variant="contained"
                  onClick={handleAdd}
                  aria-label="Add Partner Button"
                >
                  <PlusIcon />
                  <FormattedMessage
                    id="addPartner"
                    defaultMessage="Add Partner"
                  />
                </Button>,
              ]
            : undefined
        }
      />
      <StyledTable
        rows={partners || []}
        columns={columns}
        selectedRows={
          canManagePartners ? selectedPartners : undefined
        }
        onSelectedRowsChange={
          canManagePartners ? setSelectedPartners : undefined
        }
      />
      <AddPartnerModal
        gameId={gameId}
        open={addPartnerModalOpen}
        onClose={() => setAddPartnerModalOpen(false)}
        onSave={handleAddPartnerSubmit}
      />
    </>
  );
};

const StyledTable = styled(Table<Data>)({
  padding: '0 40px',
});

export default PartnersTable;
